import { z } from 'zod';
import { paginatedSchema, paginationQuerySchema } from './pagination';

/**
 * Diagnóstico empresarial (T-160, RF-10, RF-11, PRD §11.4; DEC-061): problemas, lacunas, necessidades e evidências
 * levantados no diagnóstico da Fluxus — base da matriz de priorização (T-161).
 */

/**
 * Situação do item: levantado → em análise → na matriz → em tratamento (virou iniciativa ou plano) → monitorado;
 * arquivado (com justificativa — T-161). Levantado e em análise são escolhidos no cadastro; os demais vêm das ações.
 */
export const DIAGNOSIS_STATUSES = [
  'IDENTIFIED',
  'IN_ANALYSIS',
  'IN_MATRIX',
  'IN_TREATMENT',
  'MONITORED',
  'ARCHIVED',
] as const;
export type DiagnosisStatusName = (typeof DIAGNOSIS_STATUSES)[number];
export const DIAGNOSIS_STATUS_LABELS: Record<DiagnosisStatusName, string> = {
  IDENTIFIED: 'Levantado',
  IN_ANALYSIS: 'Em análise',
  IN_MATRIX: 'Na matriz',
  IN_TREATMENT: 'Em tratamento',
  MONITORED: 'Monitorado',
  ARCHIVED: 'Arquivado',
};
/** O que a pessoa escolhe no cadastro (os demais vêm das ações: matriz, tratamento, monitorado, arquivar). */
export const DIAGNOSIS_EDITABLE_STATUSES = [
  'IDENTIFIED',
  'IN_ANALYSIS',
] as const satisfies readonly DiagnosisStatusName[];
export type DiagnosisEditableStatusName = (typeof DIAGNOSIS_EDITABLE_STATUSES)[number];
/** Situações em que o item ainda pode ir para a matriz. */
export const DIAGNOSIS_TO_MATRIX_FROM: readonly DiagnosisStatusName[] = DIAGNOSIS_EDITABLE_STATUSES;
/** Situações que passam a "em tratamento" quando o item vira iniciativa ou plano de ação. */
export const DIAGNOSIS_TREATABLE: readonly DiagnosisStatusName[] = ['IDENTIFIED', 'IN_ANALYSIS', 'IN_MATRIX'];
/** Itens da matriz de priorização: enviados à matriz e não arquivados (DEC-062). */
export const DIAGNOSIS_MATRIX_STATUSES: readonly DiagnosisStatusName[] = [
  'IN_MATRIX',
  'IN_TREATMENT',
  'MONITORED',
];
/** Situações que podem ser marcadas como monitoradas (saída da matriz). */
export const DIAGNOSIS_MONITOR_FROM: readonly DiagnosisStatusName[] = ['IN_MATRIX', 'IN_TREATMENT'];

export const DIAGNOSIS_CRITICALITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
export type DiagnosisCriticalityName = (typeof DIAGNOSIS_CRITICALITIES)[number];
export const DIAGNOSIS_CRITICALITY_LABELS: Record<DiagnosisCriticalityName, string> = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
  CRITICAL: 'Crítica',
};

/** Impacto, urgência e esforço: de 1 (menor) a 5 (maior), a escala da matriz (§11.5). */
export const DIAGNOSIS_SCALE_MIN = 1;
export const DIAGNOSIS_SCALE_MAX = 5;
const scale = (label: string) =>
  z
    .number({ message: `Informe ${label} de 1 a 5.` })
    .int(`Informe ${label} de 1 a 5.`)
    .min(DIAGNOSIS_SCALE_MIN, `Informe ${label} de 1 a 5.`)
    .max(DIAGNOSIS_SCALE_MAX, `Informe ${label} de 1 a 5.`);

const optionalText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `${label} pode ter no máximo ${max} caracteres.`)
    .nullable()
    .optional()
    .transform((v) => v || null);
const optionalId = (message: string) =>
  z
    .string()
    .uuid(message)
    .nullable()
    .optional()
    .transform((v) => v ?? null);

/** Cadastro e edição do item (todos os campos do §11.4). */
export const diagnosisInputSchema = z.object({
  title: z
    .string({ message: 'Informe o título do problema.' })
    .trim()
    .min(3, 'Informe o título do problema (pelo menos 3 caracteres).')
    .max(160, 'Título longo demais (até 160 caracteres).'),
  description: optionalText(4000, 'A descrição'),
  departmentId: z.string({ message: 'Escolha a área.' }).uuid('Escolha a área.'),
  businessUnitId: optionalId('Unidade inválida.'),
  impact: scale('o impacto'),
  urgency: scale('a urgência'),
  effort: scale('o esforço estimado'),
  criticality: z.enum(DIAGNOSIS_CRITICALITIES, { message: 'Escolha a criticidade.' }),
  surveyedById: optionalId('Responsável pelo levantamento inválido.'),
  evidence: optionalText(4000, 'A evidência'),
  notes: optionalText(4000, 'As observações'),
  status: z.enum(DIAGNOSIS_EDITABLE_STATUSES, { message: 'Escolha o status.' }).default('IDENTIFIED'),
});
export type DiagnosisInput = z.input<typeof diagnosisInputSchema>;

/** Relacionar a objetivo existente (null desfaz). */
export const diagnosisObjectiveInputSchema = z.object({
  objectiveId: z
    .string()
    .uuid('Escolha um objetivo.')
    .nullable()
    .transform((v) => v ?? null),
});
export type DiagnosisObjectiveInput = z.input<typeof diagnosisObjectiveInputSchema>;

const ref = z.object({ id: z.string().uuid(), name: z.string() });
/** O que nasceu do item (ou se liga a ele), com o nome só para quem enxerga (nulo para os demais). */
const linked = z.object({ id: z.string().uuid(), name: z.string().nullable(), archived: z.boolean() });

export const diagnosisItemSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  department: ref,
  businessUnit: ref.nullable(),
  impact: z.number().int(),
  urgency: z.number().int(),
  effort: z.number().int(),
  criticality: z.enum(DIAGNOSIS_CRITICALITIES),
  surveyedBy: ref.nullable(),
  evidence: z.string().nullable(),
  notes: z.string().nullable(),
  status: z.enum(DIAGNOSIS_STATUSES),
  /**
   * Objetivo relacionado (com o arquivado marcado). O nome só para quem enxerga o objetivo (módulo Objetivos e escopo
   * da área dele); para os demais, nulo — como a origem dos planos e das iniciativas.
   */
  objective: linked.nullable(),
  /** Iniciativa em que o item foi transformado (o vínculo fica); nome só para quem enxerga a iniciativa. */
  initiative: linked.nullable(),
  /** Planos de ação gerados a partir do item (origem "item de diagnóstico"); nome só para quem enxerga o plano. */
  plans: z.array(linked),
  sentToMatrixAt: z.string().nullable(),
  /** Arquivamento (saída da matriz — DEC-062): quando, por quem e a justificativa. */
  archive: z.object({ at: z.string(), byName: z.string(), reason: z.string() }).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DiagnosisItem = z.infer<typeof diagnosisItemSchema>;

export const diagnosisListQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(100, 'Busca longa demais.').optional(),
  departmentId: z.string().uuid().optional(),
  businessUnitId: z.string().uuid().optional(),
  criticality: z.enum(DIAGNOSIS_CRITICALITIES).optional(),
  status: z.enum(DIAGNOSIS_STATUSES).optional(),
});
export type DiagnosisListQuery = z.input<typeof diagnosisListQuerySchema>;
export const diagnosisListSchema = paginatedSchema(diagnosisItemSchema);

// ---------------------------------------------------------------------------------------------
// Matriz de priorização (T-161, RF-12, RF-13, RN-25, PRD §11.5; DEC-004 item 5, DEC-062)

/** Pontuação da matriz (RN-25): impacto × urgência ÷ esforço. */
export const priorityScore = (impact: number, urgency: number, effort: number): number =>
  (impact * urgency) / effort;

type Rankable = {
  id: string;
  impact: number;
  urgency: number;
  effort: number;
  sentToMatrixAt: string | null;
};

/**
 * Ranking da matriz (RN-25; DEC-004 item 5): maior pontuação primeiro; empate pelo maior impacto, depois pela maior
 * urgência e, por fim, pelo envio à matriz mais antigo. A prioridade é a posição (1 = tratar primeiro).
 */
export function rankByPriority<T extends Rankable>(
  items: readonly T[],
): (T & { score: number; priority: number })[] {
  return items
    .map((i) => ({ ...i, score: priorityScore(i.impact, i.urgency, i.effort) }))
    .sort(
      (a, b) =>
        b.score - a.score ||
        b.impact - a.impact ||
        b.urgency - a.urgency ||
        (a.sentToMatrixAt ?? '').localeCompare(b.sentToMatrixAt ?? '') ||
        a.id.localeCompare(b.id),
    )
    .map((i, index) => ({ ...i, priority: index + 1 }));
}

export const matrixItemSchema = diagnosisItemSchema.extend({
  /** Impacto × urgência ÷ esforço. */
  score: z.number(),
  /** Posição no ranking da matriz do projeto (1 = tratar primeiro). */
  priority: z.number().int(),
});
export type MatrixItem = z.infer<typeof matrixItemSchema>;
export const matrixQuerySchema = z.object({
  departmentId: z.string().uuid().optional(),
  businessUnitId: z.string().uuid().optional(),
});
export type MatrixQuery = z.input<typeof matrixQuerySchema>;
/** Itens da matriz em ordem de prioridade; `total` conta todos os itens da matriz visíveis (sem os filtros). */
export const matrixListSchema = z.object({ items: z.array(matrixItemSchema), total: z.number().int() });
export type MatrixList = z.infer<typeof matrixListSchema>;

export const DIAGNOSIS_ARCHIVE_REASON_MAX = 2000;
/** Arquivar o item (saída da matriz): justificativa obrigatória. */
export const diagnosisArchiveInputSchema = z.object({
  reason: z
    .string({ message: 'Informe a justificativa do arquivamento.' })
    .trim()
    .min(3, 'Informe a justificativa do arquivamento (pelo menos 3 caracteres).')
    .max(
      DIAGNOSIS_ARCHIVE_REASON_MAX,
      `Justificativa com no máximo ${DIAGNOSIS_ARCHIVE_REASON_MAX} caracteres.`,
    ),
});
export type DiagnosisArchiveInput = z.input<typeof diagnosisArchiveInputSchema>;
export type DiagnosisList = z.infer<typeof diagnosisListSchema>;

/** Opções do cadastro e dos filtros: áreas e unidades no escopo, pessoas e objetivos ativos do projeto. */
export const diagnosisOptionsSchema = z.object({
  departments: z.array(ref),
  businessUnits: z.array(ref),
  /** Responsáveis possíveis pelo levantamento: pessoas ativas da empresa, o consultor do projeto e quem pede. */
  people: z.array(ref),
  /** Objetivos ativos no escopo (vazio sem acesso ao módulo Objetivos). */
  objectives: z.array(ref.extend({ departmentId: z.string().uuid().nullable() })),
});
export type DiagnosisOptions = z.infer<typeof diagnosisOptionsSchema>;
